import { Flex, Skeleton, Spinner, Text } from "components/common"
import { t } from "i18next"
import { memo, useMemo, useState } from "react"
import styles from "../index.module.less"
import { addDateSeparators, formatMessageTime, getMessageStatus, getTickIcon, safeJSONParse,getLocalizedWhatsAppMessage } from "../utils"
import { BasicCard } from "./cards/basicCard"
import WaImage from "./ImageComponent"
import Location from "./location/location"
import WaVideo from "./VideoComponent"
import { WhatsappAudioMessage } from "./whatsappAudioMessage/whatsappAudioMessage"
import { WhatsappDocument } from "./whatsappDocument/whatsappDocument"

const ExpandableText = ({ message, timeAndTick: msg,translations }) => {
  const isMe = msg?.sentBy === "staff"

  
  const TimeAndTick = () => {
    if (!msg) return null
    return (
      <>
        <span className={styles.msgTime}>{formatMessageTime(msg?.messageTimestamp)}</span>
        {isMe && getTickIcon(getMessageStatus(msg), styles)}
      </>
    )
  }
  const [isExpanded, setIsExpanded] = useState(false)
  const CHARACTER_LIMIT = 500

  if (!message || message.length <= CHARACTER_LIMIT) {
    return (
      <div className={styles.textMessageContainer}>
        <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
            {getLocalizedWhatsAppMessage(message, translations?.enTranslation, translations?.arTranslation)}
        </span>
        <span className={styles.inlineTimeAndTick}>
          <TimeAndTick />
        </span>
      </div>
    )
  }

  const truncatedTextWithoutEllipsis = message?.slice(0, CHARACTER_LIMIT - 3)

  return (
    <div className={styles.textMessageContainer}>
      <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
        {isExpanded ? (
          <>
            {getLocalizedWhatsAppMessage(message, translations?.enTranslation, translations?.arTranslation)}
            <span className={styles.expandableTextButton} onClick={() => setIsExpanded(false)}>
              {t("See Less")}
            </span>
          </>
        ) : (
          <>
            {truncatedTextWithoutEllipsis}...
            <span
              className={styles.expandableTextButton}
              onClick={() => setIsExpanded(true)}
            >
              {t("See More")}
            </span>
          </>
        )}
      </span>
      <span className={styles.inlineTimeAndTick}>
        <TimeAndTick />
      </span>
    </div>
  )
}

const ChatBody = memo(({ chatMessages, isLoading = false, isLoadingMore = false, client, selectedStaff }) => {
  const parsedContentType = content => safeJSONParse(content)

  const generateSkeletonMessages = () => {
    return Array.from({ length: 9 }, (_, i) => {
      const isStaff = i % 1.5 === 1
      return {
        id: `skeleton-${i}`,
        sentBy: isStaff ? "staff" : "client",
        content: JSON.stringify({
          type: "text"
        })
      }
    })
  }

  const skeletonMessages = useMemo(() => generateSkeletonMessages(), [])

  const messagesWithDateSeparators = useMemo(() => {
    if (isLoading) {
      return skeletonMessages
    }
    return addDateSeparators(chatMessages)
  }, [chatMessages, isLoading, skeletonMessages])

  return (
    <Flex vertical gap={8} className={styles.chatBody}>
      <Flex vertical gap={8} className={styles.chatBg}>
        {isLoadingMore && !isLoading && (
          <Flex justify="center" className={styles.loadingMoreSpinner}>
            <Spinner />
          </Flex>
        )}
        {messagesWithDateSeparators.map(msg => {
          const parsedContent = parsedContentType(msg.content)
          if (isLoading) {
            return (
              <BasicCard key={msg.id} msg={msg} isLoading={true}>
                <Skeleton
                  active
                  width={350}
                  height={25}
                />
              </BasicCard>
            )
          }
          if (msg.type === "date" || parsedContent?.type === "date") {
            return (
              <Flex key={msg.id} justify="center" className={styles.dateDivider}>
                <Text type="secondary" className={styles.dateSeparator}>
                  {msg.text || parsedContent?.date}
                </Text>
              </Flex>
            )
          }
          const messageComponents = {
            text: () => (
              <ExpandableText
                message={parsedContent?.message || parsedContent?.text}
                timeAndTick={msg}
                translations={{
                  enTranslation: msg?.translationEn,
                  arTranslation: msg?.translationAr,
                }}
              />
            ),
            link: () => <ExpandableText message={parsedContent?.text} timeAndTick={msg} />,
            document: () => <WhatsappDocument parsedContent={parsedContent} />,
            video: () => <WaVideo parsedContent={parsedContent} />,
            image: () => <WaImage parsedContent={parsedContent} />,
            audio: () => <WhatsappAudioMessage audioUrl={parsedContent?.s3KeyValue} duration={parsedContent?.seconds} />,
            live_location: () => <Location parsedContent={parsedContent} />,
            location: () => <Location parsedContent={parsedContent} />
          }
          const messageType = parsedContent?.type
          const Component = messageComponents[messageType]
          if (Component) {
            const cardProps = messageType === "text" || messageType === "link" ? {
                    removeExtraPadding: true,
                    overlayTimeAndTick: false,
                    hideTimeAndTick: true,
            } : {
                    removeExtraPadding: true,
                    overlayTimeAndTick: true,
              className: (messageType === "video" || messageType === "image") ? styles.videoImageOverlay : "",
              ...(messageType === "video" || messageType === "document" ? { videoDuration: parsedContent?.seconds || 0 } : {})
                  }

            return (
              <BasicCard key={msg.id} msg={msg} cardProps={cardProps} client={client} selectedStaff={selectedStaff}>
                <Component />
              </BasicCard>
            )
          }
        })}
      </Flex>
    </Flex>
  )
})

export default ChatBody


